import { useState, useEffect } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/utils/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Scissors, Sparkles } from "lucide-react";

export default function SignupPage() {
  const { user, signup } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [inviteValid, setInviteValid] = useState(null); // null=loading, true=valid, "used"=already used, false=invalid
  const [courseName, setCourseName] = useState("");
  const [usedMessage, setUsedMessage] = useState("");

  useEffect(() => {
    if (!inviteToken) {
      setInviteValid(false);
      return;
    }
    api.get(`/invites/verify/${inviteToken}`)
      .then(({ data }) => {
        setName(data.name || "");
        setEmail(data.email || "");
        setInviteValid(true);
        // Get course name
        api.get(`/courses/${data.course_id}`).then(({ data: c }) => setCourseName(c.title)).catch(() => {});
      })
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        if (status === 410) {
          setInviteValid("used");
          setUsedMessage(detail);
        } else {
          setInviteValid(false);
        }
      });
  }, [inviteToken]);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;

  // No invite token or invalid
  if (!inviteToken || inviteValid === false) {
    return (
      <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5C3A2E] rounded-2xl mb-5">
            <Scissors className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-[#5C3A2E] font-semibold mb-3">Invitation Required</h1>
          <p className="text-[#2B2B2B]/60 text-sm mb-6">
            {inviteToken ? "This invitation link is invalid or has expired." : "Kalemash Studio is invite-only. Please contact your studio manager for an invitation."}
          </p>
          <Link to="/login">
            <Button className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">Back to Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Invite was already used
  if (inviteValid === "used") {
    return (
      <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5C3A2E] rounded-2xl mb-5">
            <Scissors className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-[#5C3A2E] font-semibold mb-3">Already Registered</h1>
          <p className="text-[#2B2B2B]/60 text-sm mb-6">
            {usedMessage || "This invitation has already been used. If you already created your account, please sign in."}
          </p>
          <Link to="/login">
            <Button className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Loading invite
  if (inviteValid === null) {
    return (
      <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center">
        <div className="text-[#5C3A2E]/50 font-body">Verifying invitation...</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      await signup(name, email, password, inviteToken);
      // AuthContext will set user, Navigate will redirect
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 animate-fade-in-up">
          <img src="/kalemash-logo.png" alt="Kalemash" className="w-16 h-16 mx-auto mb-5 rounded-2xl" />
          <h1 className="font-heading text-4xl sm:text-5xl text-[#5C3A2E] font-semibold tracking-tight">
            Welcome to Kalemash
          </h1>
          {courseName && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#EADBC8]/50 rounded-full">
              <Sparkles className="w-4 h-4 text-[#C6A969]" />
              <span className="text-sm text-[#5C3A2E]">{courseName}</span>
            </div>
          )}
          <p className="text-[#2B2B2B]/60 mt-3 font-body text-base">Set up your account to begin your creative journey</p>
        </div>

        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] p-8 animate-fade-in-up animate-stagger-2">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="signup-error" className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-100">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Full Name</label>
              <Input
                data-testid="signup-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border-[#EADBC8] bg-white px-4 py-3 text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Email</label>
              <Input
                data-testid="signup-email-input"
                type="email"
                value={email}
                readOnly
                className="w-full rounded-xl border-[#EADBC8] bg-[#F7F3EF] px-4 py-3 text-[#2B2B2B]/70 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Password</label>
              <Input
                data-testid="signup-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="w-full rounded-xl border-[#EADBC8] bg-white px-4 py-3 text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent"
                required
              />
            </div>
            <Button
              data-testid="signup-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#5C3A2E] text-white rounded-full py-3 hover:bg-[#5C3A2E]/90 transition-all font-medium text-base h-12"
            >
              {submitting ? "Setting up..." : "Join the Studio"}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-[#2B2B2B]/60">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-[#5C3A2E] font-medium hover:text-[#C6A969] transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
